import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, shareReplay, tap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Collection } from './models/api-response.model';
import { Tenant } from './models/user.model';

@Injectable({ providedIn: 'root' })
export class TenantApiService {
  private static readonly TENANTS_CACHE_TTL_MS = 5 * 60 * 1000;
  private http = inject(HttpClient);
  private cachedTenants$?: Observable<Collection<Tenant>>;
  private tenantsCacheExpiresAt = 0;

  getTenants(): Observable<Collection<Tenant>> {
    const now = Date.now();
    if (this.cachedTenants$ && this.tenantsCacheExpiresAt > now) {
      return this.cachedTenants$;
    }

    if (!this.cachedTenants$ || this.tenantsCacheExpiresAt <= now) {
      this.cachedTenants$ = this.http.get<Collection<Tenant>>(`${environment.api}/tenants`).pipe(
        tap(() => {
          this.tenantsCacheExpiresAt = Date.now() + TenantApiService.TENANTS_CACHE_TTL_MS;
        }),
        catchError((error) => {
          this.clearTenantsCache();
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.cachedTenants$;
  }

  updateTenantName(tenantKey: string, tenantName: string): Observable<Tenant> {
    return this.http
      .patch<Tenant>(`${environment.api}/tenants/${encodeURIComponent(tenantKey)}`, {
        tenant_name: tenantName,
      })
      .pipe(tap(() => this.clearTenantsCache()));
  }

  clearTenantsCache(): void {
    this.cachedTenants$ = undefined;
    this.tenantsCacheExpiresAt = 0;
  }
}
